/**
 * Quick Editor: the timeline model and its editing operations.
 *
 * A project is a list of media assets, tracks (video tracks on top, audio tracks
 * below) and clips. A clip shows the part of an asset between `inSec` and
 * `outSec`, starting at `startSec` on its track. A video clip and the audio that
 * came with it share a `linkId`: moving, trimming or cutting one does the same
 * to the other, so picture and sound stay together until they are unlinked.
 *
 * Everything here is a pure function from one project to the next; the node
 * keeps the project in its data and applies these on user gestures.
 */

export type TrackKind = 'video' | 'audio';

export type EditorAssetSource =
    /** A file added from disk, kept in this browser's local media store under `key`. */
    | { type: 'local'; key: string }
    /** Media from the library or the canvas (a file:// reference, a URL or a blob URL). */
    | { type: 'ref'; ref: string };

export interface EditorAsset {
    id: string;
    name: string;
    kind: TrackKind;
    durationSec: number;
    /** Video files are assumed to carry sound; their clips get a linked audio clip. */
    hasAudio: boolean;
    width?: number;
    height?: number;
    /** Video: the rate it was made at, read from the file (generated clips are mostly 24). */
    frameRate?: number;
    /** Video: how long the picture runs, when that is shorter than the file (its sound runs on). */
    videoDurationSec?: number;
    sizeBytes?: number;
    mimeType?: string;
    source: EditorAssetSource;
}

export interface EditorTrack {
    id: string;
    kind: TrackKind;
    name: string;
    /** Audio tracks: silent in the preview. */
    muted?: boolean;
    /** Video tracks: skipped in the preview. */
    hidden?: boolean;
    /** Video tracks: how opaque the layer is over the tracks below, 0 to 1 (1 when unset). */
    opacity?: number;
    /** Audio tracks: the track's loudness in the mix, 0 to 1 (1 when unset). */
    volume?: number;
}

export interface EditorClip {
    id: string;
    assetId: string;
    trackId: string;
    /** Position on the timeline. */
    startSec: number;
    /** The part of the asset that is used. */
    inSec: number;
    outSec: number;
    /** Shared by a video clip and its audio. */
    linkId?: string;
    /** Audio clips: how much quieter this clip plays, in decibels (0 or less; 0 when unset). */
    gainDb?: number;
}

export interface QuickEditorProject {
    version: 1;
    assets: EditorAsset[];
    /** Display order, top to bottom: video tracks first (each drawn over the ones below it), then audio tracks. */
    tracks: EditorTrack[];
    clips: EditorClip[];
    /** Timeline zoom. */
    pxPerSec: number;
    snapping: boolean;
    /** In and out points: the part of the edit that is exported. Unset means from the start, or to the end. */
    inSec?: number;
    outSec?: number;
}

export const MIN_CLIP_SEC = 0.1;
export const MIN_PX_PER_SEC = 8;
export const MAX_PX_PER_SEC = 400;
const EPS = 1e-6;

const round3 = (value: number): number => Math.round(value * 1000) / 1000;
const clamp = (value: number, lo: number, hi: number): number => Math.min(Math.max(value, lo), hi);

// ── Frames ──────────────────────────────────────────────────────────────────
// The timeline runs at the frame rate of its video. Once that is known, every time an edit
// sets (where a clip goes, a cut, a trimmed edge, the in and out points) is a frame start,
// so cuts fall between frames and the preview and export show whole frames.

/** The rate used for stepping, timecode and export when no video on the timeline says what it was made at. */
export const DEFAULT_FRAME_RATE = 30;

/**
 * The frame rate the edit runs at: that of the video on its video tracks (the rate covering
 * the most time, when clips differ), else that of the video in its media list; null when no
 * video says, and then edits are not put on a frame grid.
 */
export const timelineFrameRate = (project: QuickEditorProject): number | null => {
    const timeAt = new Map<number, number>();
    const count = (rate: number | undefined, sec: number) => { if (rate) timeAt.set(rate, (timeAt.get(rate) ?? 0) + sec); };
    for (const clip of project.clips) {
        const asset = project.assets.find(a => a.id === clip.assetId);
        if (asset?.kind === 'video' && project.tracks.find(t => t.id === clip.trackId)?.kind === 'video') count(asset.frameRate, clip.outSec - clip.inSec);
    }
    if (timeAt.size === 0) for (const asset of project.assets) if (asset.kind === 'video') count(asset.frameRate, asset.durationSec);
    let best: number | null = null;
    let most = -Infinity;
    for (const [rate, sec] of timeAt) {
        if (sec > most + EPS || (Math.abs(sec - most) <= EPS && rate > (best ?? 0))) { best = rate; most = sec; }
    }
    return best;
};

/** The timeline's frame rate for stepping, timecode and export. */
export const projectFrameRate = (project: QuickEditorProject): number => timelineFrameRate(project) ?? DEFAULT_FRAME_RATE;

/** The start of the frame nearest `time`. */
export const toFrame = (time: number, fps: number): number => round3(Math.round(time * fps) / fps);

/** The start of the frame at or after `time` (a time within a millisecond of a frame start is that frame). */
const toFrameAtOrAfter = (time: number, fps: number): number => round3(Math.ceil(time * fps - 1e-3) / fps);

/** `time` on the edit's frame grid, when it has one. */
const onGrid = (project: QuickEditorProject, time: number, round: 'nearest' | 'up' = 'nearest'): number => {
    const fps = timelineFrameRate(project);
    if (!fps) return time;
    return round === 'up' ? toFrameAtOrAfter(time, fps) : toFrame(time, fps);
};

/**
 * How much of an asset a clip can show: a video's picture, in whole frames when its rate is
 * known (its sound may run on past the last frame, which would leave a clip's end between
 * frames), else all of it.
 */
export const usableDuration = (asset: Pick<EditorAsset, 'kind' | 'durationSec' | 'frameRate' | 'videoDurationSec'>): number => {
    if (asset.kind !== 'video') return asset.durationSec;
    const picture = asset.videoDurationSec && asset.videoDurationSec < asset.durationSec ? asset.videoDurationSec : asset.durationSec;
    return asset.frameRate ? round3(Math.max(1, Math.floor(picture * asset.frameRate + 1e-3)) / asset.frameRate) : picture;
};

export const clipDuration = (clip: EditorClip): number => clip.outSec - clip.inSec;
export const clipEnd = (clip: EditorClip): number => clip.startSec + clipDuration(clip);

export const projectDuration = (project: QuickEditorProject): number =>
    project.clips.reduce((max, clip) => Math.max(max, clipEnd(clip)), 0);

export const createEmptyProject = (): QuickEditorProject => ({
    version: 1,
    assets: [],
    tracks: [
        { id: 'v1', kind: 'video', name: 'V1' },
        { id: 'a1', kind: 'audio', name: 'A1' },
    ],
    clips: [],
    pxPerSec: 60,
    snapping: true,
});

const trackOf = (project: QuickEditorProject, trackId: string): EditorTrack | undefined =>
    project.tracks.find(track => track.id === trackId);

const clipsOnTrack = (project: QuickEditorProject, trackId: string, ignore: Set<string>): EditorClip[] =>
    project.clips.filter(clip => clip.trackId === trackId && !ignore.has(clip.id)).sort((a, b) => a.startSec - b.startSec);

/** The clip together with whatever is linked to it. */
export const linkedGroup = (project: QuickEditorProject, clipId: string): EditorClip[] => {
    const clip = project.clips.find(c => c.id === clipId);
    if (!clip) return [];
    return clip.linkId ? project.clips.filter(c => c.linkId === clip.linkId) : [clip];
};

/**
 * The start nearest to `desiredStart` at which a clip of `duration` fits on the
 * track without overlapping another clip.
 */
export const placeOnTrack = (
    project: QuickEditorProject,
    trackId: string,
    desiredStart: number,
    duration: number,
    ignoreIds: string[] = [],
): number => {
    const others = clipsOnTrack(project, trackId, new Set(ignoreIds));
    const want = Math.max(0, desiredStart);
    let best: number | null = null;
    const consider = (gapStart: number, gapEnd: number) => {
        if (gapEnd - gapStart + EPS < duration) return;
        const start = Math.min(Math.max(want, gapStart), gapEnd - duration);
        if (best === null || Math.abs(start - want) < Math.abs(best - want)) best = start;
    };
    let cursor = 0;
    for (const other of others) {
        consider(cursor, other.startSec);
        cursor = Math.max(cursor, clipEnd(other));
    }
    consider(cursor, Infinity);
    return round3(best ?? cursor);
};

/** Register a media asset with the project (no clip is created). */
export const addAsset = (project: QuickEditorProject, asset: EditorAsset): QuickEditorProject =>
    project.assets.some(a => a.id === asset.id) ? project : { ...project, assets: [...project.assets, asset] };

/** Remove an asset and every clip that uses it. */
export const removeAsset = (project: QuickEditorProject, assetId: string): QuickEditorProject => ({
    ...project,
    assets: project.assets.filter(a => a.id !== assetId),
    clips: project.clips.filter(c => c.assetId !== assetId),
});

/** Update an asset's measured properties (for example after a relink). */
export const updateAsset = (project: QuickEditorProject, assetId: string, patch: Partial<EditorAsset>): QuickEditorProject => ({
    ...project,
    assets: project.assets.map(a => (a.id === assetId ? { ...a, ...patch } : a)),
});

/** Add a track: video tracks go on top, audio tracks at the bottom. */
export const addTrack = (project: QuickEditorProject, kind: TrackKind, makeId: () => string): QuickEditorProject => {
    const count = project.tracks.filter(t => t.kind === kind).length;
    const track: EditorTrack = { id: makeId(), kind, name: `${kind === 'video' ? 'V' : 'A'}${count + 1}` };
    return { ...project, tracks: kind === 'video' ? [track, ...project.tracks] : [...project.tracks, track] };
};

/** Remove an empty track; the last track of a kind stays. */
export const removeTrack = (project: QuickEditorProject, trackId: string): QuickEditorProject => {
    const track = trackOf(project, trackId);
    if (!track) return project;
    if (project.clips.some(c => c.trackId === trackId)) return project;
    if (project.tracks.filter(t => t.kind === track.kind).length <= 1) return project;
    return { ...project, tracks: project.tracks.filter(t => t.id !== trackId) };
};

export const setTrackFlag = (project: QuickEditorProject, trackId: string, flag: 'muted' | 'hidden', value: boolean): QuickEditorProject => ({
    ...project,
    tracks: project.tracks.map(t => (t.id === trackId ? { ...t, [flag]: value } : t)),
});

/** A track's level: a video layer's opacity or an audio track's volume, 0 to 1. */
export const trackLevel = (track: EditorTrack): number =>
    clamp(track.kind === 'video' ? track.opacity ?? 1 : track.volume ?? 1, 0, 1);

/** Set a video layer's opacity or an audio track's volume. */
export const setTrackLevel = (project: QuickEditorProject, trackId: string, level: number): QuickEditorProject => {
    const value = Math.round(clamp(level, 0, 1) * 100) / 100;
    return {
        ...project,
        tracks: project.tracks.map(t => (t.id !== trackId ? t : t.kind === 'video' ? { ...t, opacity: value } : { ...t, volume: value })),
    };
};

/** Where `insertAsset` puts a clip: on a given track, or on a new one. */
export interface InsertPlacement {
    trackId?: string;
    newTrack?: boolean;
}

const isFreeAt = (project: QuickEditorProject, trackId: string, start: number, duration: number): boolean =>
    Math.abs(placeOnTrack(project, trackId, start, duration) - start) < 1e-3;

/**
 * Put an asset on the timeline at `atSec`. Unless told which track, the clip goes on the
 * first track of its kind that is free at that time (the bottom video layer first, then
 * the ones above; the top audio track first, then those below), and a new track is added
 * when none is: so media added at a time that is taken is layered, not pushed along.
 * On a given track it lands at the nearest free spot there. A video with sound brings a
 * linked audio clip at the same time on the first audio track that is free there.
 */
export const insertAsset = (
    project: QuickEditorProject,
    assetId: string,
    atSec: number,
    makeId: () => string,
    placement: InsertPlacement = {},
): QuickEditorProject => {
    const asset = project.assets.find(a => a.id === assetId);
    if (!asset) return project;
    const duration = Math.max(MIN_CLIP_SEC, usableDuration(asset));
    // Rounded up, never back over the end of a clip already there
    const at = round3(Math.max(0, onGrid(project, atSec, 'up')));
    let next = project;
    const tracksOf = (kind: TrackKind) => next.tracks.filter(t => t.kind === kind);
    // A new video track goes on top, a new audio track at the bottom
    const addNew = (kind: TrackKind): EditorTrack => {
        next = addTrack(next, kind, makeId);
        const list = tracksOf(kind);
        return kind === 'video' ? list[0] : list[list.length - 1];
    };
    const freeTrack = (kind: TrackKind, start: number): EditorTrack => {
        const order = kind === 'video' ? [...tracksOf('video')].reverse() : tracksOf('audio');
        return order.find(t => isFreeAt(next, t.id, start, duration)) ?? addNew(kind);
    };

    const asked = placement.trackId ? tracksOf(asset.kind).find(t => t.id === placement.trackId) : undefined;
    const track = placement.newTrack ? addNew(asset.kind) : asked ?? freeTrack(asset.kind, at);
    const start = asked ? placeOnTrack(next, track.id, at, duration) : at;
    const clip: EditorClip = { id: makeId(), assetId, trackId: track.id, startSec: start, inSec: 0, outSec: round3(duration) };
    if (asset.kind === 'audio' || !asset.hasAudio) return { ...next, clips: [...next.clips, clip] };

    const audioTrack = freeTrack('audio', start);
    const linkId = makeId();
    return {
        ...next,
        clips: [
            ...next.clips,
            { ...clip, linkId },
            { id: makeId(), assetId, trackId: audioTrack.id, startSec: start, inSec: 0, outSec: round3(duration), linkId },
        ],
    };
};

/**
 * Move a clip (and what is linked to it) to the position nearest `desiredStart`
 * where the whole group fits. The clip may change to another track of its kind.
 * When no position keeps the group aligned, nothing changes.
 */
export const moveClip = (project: QuickEditorProject, clipId: string, desiredStart: number, targetTrackId?: string): QuickEditorProject =>
    moveClips(project, [clipId], clipId, desiredStart, targetTrackId);

/**
 * Move several clips together, keeping their spacing: the grabbed clip goes to the position
 * nearest `desiredStart` where every moving clip fits, and whatever is linked to them comes
 * along. When `targetTrackId` is another track of the grabbed clip's kind, the chosen clips of
 * that kind go up or down by as many tracks, if all of them can; linked clips keep their
 * tracks. When no position fits them all, nothing changes.
 */
export const moveClips = (
    project: QuickEditorProject,
    clipIds: string[],
    grabbedId: string,
    desiredStart: number,
    targetTrackId?: string,
): QuickEditorProject => {
    const clip = project.clips.find(c => c.id === grabbedId);
    if (!clip) return project;
    desiredStart = onGrid(project, desiredStart);
    const chosen = new Set([grabbedId, ...clipIds.filter(id => project.clips.some(c => c.id === id))]);
    const moving = new Map<string, EditorClip>();
    for (const id of chosen) for (const member of linkedGroup(project, id)) moving.set(member.id, member);
    const group = [...moving.values()];
    const ids = [...moving.keys()];

    // Changing track: the chosen clips of the grabbed clip's kind shift by the same number of lanes
    const ownKind = trackOf(project, clip.trackId)?.kind;
    const lanes = project.tracks.filter(track => track.kind === ownKind).map(track => track.id);
    const shifts = (member: EditorClip) => chosen.has(member.id) && trackOf(project, member.trackId)?.kind === ownKind;
    let offset = targetTrackId && lanes.includes(targetTrackId) ? lanes.indexOf(targetTrackId) - lanes.indexOf(clip.trackId) : 0;
    const laneFor = (member: EditorClip, by: number) => (by !== 0 && shifts(member) ? lanes[lanes.indexOf(member.trackId) + by] : member.trackId);
    if (offset !== 0) {
        const landed = group.map(member => ({ member, trackId: laneFor(member, offset) }));
        const offTheEnd = landed.some(({ trackId }) => !trackId);
        // Moving clips may not end up on top of each other
        const collide = landed.some((a, i) => landed.some((b, j) => j > i && a.trackId === b.trackId
            && a.member.startSec < clipEnd(b.member) - EPS && b.member.startSec < clipEnd(a.member) - EPS));
        if (offTheEnd || collide) offset = 0;
    }
    const trackFor = (member: EditorClip) => laneFor(member, offset);

    let delta = placeOnTrack(project, trackFor(clip), desiredStart, clipDuration(clip), ids) - clip.startSec;
    let settled = false;
    for (let pass = 0; pass < 8 && !settled; pass++) {
        settled = true;
        for (const member of group) {
            const want = member.startSec + delta;
            const got = placeOnTrack(project, trackFor(member), want, clipDuration(member), ids);
            if (Math.abs(got - want) > 1e-3) {
                delta = got - member.startSec;
                settled = false;
            }
        }
    }
    if (!settled) return project;
    if (Math.abs(delta) < 1e-6 && offset === 0) return project;
    return {
        ...project,
        clips: project.clips.map(c => (moving.has(c.id) ? { ...c, startSec: round3(c.startSec + delta), trackId: trackFor(c) } : c)),
    };
};

/**
 * Drag one edge of a clip to `toSec`. The edge stops at the limits of the source
 * media, at the neighbouring clip, and at the minimum clip length; linked clips
 * are trimmed by the same amount.
 */
export const trimClip = (project: QuickEditorProject, clipId: string, edge: 'start' | 'end', toSec: number): QuickEditorProject => {
    const clip = project.clips.find(c => c.id === clipId);
    if (!clip) return project;
    toSec = onGrid(project, toSec);
    const group = linkedGroup(project, clipId);
    const ids = new Set(group.map(c => c.id));
    // Up to the asset's last whole frame (never less than the clip already shows)
    const assetDuration = (member: EditorClip) => {
        const asset = project.assets.find(a => a.id === member.assetId);
        return asset ? Math.max(usableDuration(asset), member.outSec) : member.outSec;
    };

    let lo = -Infinity;
    let hi = Infinity;
    if (edge === 'start') {
        for (const member of group) {
            const previousEnd = clipsOnTrack(project, member.trackId, ids)
                .filter(other => clipEnd(other) <= member.startSec + EPS)
                .reduce((max, other) => Math.max(max, clipEnd(other)), 0);
            lo = Math.max(lo, -member.inSec, previousEnd - member.startSec, -member.startSec);
            hi = Math.min(hi, clipDuration(member) - MIN_CLIP_SEC);
        }
        const delta = clamp(toSec - clip.startSec, lo, hi);
        if (Math.abs(delta) < 1e-6) return project;
        return {
            ...project,
            clips: project.clips.map(c => (ids.has(c.id) ? { ...c, startSec: round3(c.startSec + delta), inSec: round3(c.inSec + delta) } : c)),
        };
    }

    for (const member of group) {
        const end = clipEnd(member);
        const nextStart = clipsOnTrack(project, member.trackId, ids)
            .filter(other => other.startSec >= end - EPS)
            .reduce((min, other) => Math.min(min, other.startSec), Infinity);
        lo = Math.max(lo, -(clipDuration(member) - MIN_CLIP_SEC));
        hi = Math.min(hi, assetDuration(member) - member.outSec, nextStart - end);
    }
    const delta = clamp(toSec - clipEnd(clip), lo, hi);
    if (Math.abs(delta) < 1e-6) return project;
    return {
        ...project,
        clips: project.clips.map(c => (ids.has(c.id) ? { ...c, outSec: round3(c.outSec + delta) } : c)),
    };
};

/**
 * Cut a clip in two at `atSec`. Linked clips that span the cut are cut as well,
 * and the right-hand parts stay linked to each other.
 */
export const splitClip = (project: QuickEditorProject, clipId: string, atSec: number, makeId: () => string): QuickEditorProject => {
    atSec = onGrid(project, atSec);
    const group = linkedGroup(project, clipId);
    const spans = (clip: EditorClip) => atSec >= clip.startSec + MIN_CLIP_SEC - EPS && atSec <= clipEnd(clip) - MIN_CLIP_SEC + EPS;
    const cut = group.filter(spans);
    if (!cut.some(c => c.id === clipId)) return project;
    const cutIds = new Set(cut.map(c => c.id));
    const rightLink = cut.length > 1 ? makeId() : undefined;
    return {
        ...project,
        clips: project.clips.flatMap((clip) => {
            if (!cutIds.has(clip.id)) return [clip];
            const at = round3(clip.inSec + (atSec - clip.startSec));
            const left: EditorClip = { ...clip, outSec: at };
            const { linkId: _dropped, ...rest } = clip;
            const right: EditorClip = { ...rest, id: makeId(), startSec: round3(atSec), inSec: at, ...(rightLink ? { linkId: rightLink } : {}) };
            return [left, right];
        }),
    };
};

/** Delete clips; by default what is linked to them goes too. */
export const deleteClips = (project: QuickEditorProject, clipIds: string[], withLinked = true): QuickEditorProject => {
    const doomed = new Set<string>();
    for (const id of clipIds) {
        for (const member of withLinked ? linkedGroup(project, id) : project.clips.filter(c => c.id === id)) doomed.add(member.id);
    }
    if (doomed.size === 0) return project;
    return { ...project, clips: project.clips.filter(c => !doomed.has(c.id)) };
};

/**
 * Delete clips and close the gaps they leave, so the rest of the cut keeps its rhythm: what
 * starts after a gap, on any track, moves back by the gap's length, each linked group as one
 * (a video clip never parts from its sound), and only as far as its tracks are free. What
 * starts before the gap stays where it is: a music bed under the cut keeps its place, and
 * what runs on into the gap holds back the clips behind it on its track.
 */
export const rippleDeleteClips = (project: QuickEditorProject, clipIds: string[]): QuickEditorProject => {
    const after = deleteClips(project, clipIds);
    if (after === project) return project;
    const gone = project.clips.filter(c => !after.clips.some(kept => kept.id === c.id));
    // The gaps, merged where the deleted clips overlapped in time, latest first: the clips an
    // earlier gap moves include those a later gap moved already.
    const gaps: { startSec: number; endSec: number }[] = [];
    for (const clip of [...gone].sort((a, b) => a.startSec - b.startSec)) {
        const last = gaps[gaps.length - 1];
        if (last && clip.startSec <= last.endSec + EPS) last.endSec = Math.max(last.endSec, clipEnd(clip));
        else gaps.push({ startSec: clip.startSec, endSec: clipEnd(clip) });
    }
    let clips = after.clips;
    for (const gap of gaps.reverse()) {
        const length = gap.endSec - gap.startSec;
        // What moves: every group whose members all start at or after the gap, earliest first
        const groups: EditorClip[][] = [];
        const seen = new Set<string>();
        for (const clip of [...clips].sort((a, b) => a.startSec - b.startSec)) {
            if (seen.has(clip.id)) continue;
            const group = linkedGroup({ ...after, clips }, clip.id);
            for (const member of group) seen.add(member.id);
            if (group.every(member => member.startSec >= gap.endSec - EPS)) groups.push(group);
        }
        for (const group of groups) {
            const ids = new Set(group.map(member => member.id));
            // As far back as the gap, and no further than what is in front of each member on its track
            const shift = group.reduce((least, member) => {
                const inFront = clips.filter(other => other.trackId === member.trackId && !ids.has(other.id) && other.startSec < member.startSec + EPS);
                const blocker = Math.max(0, ...inFront.map(clipEnd));
                return Math.min(least, member.startSec - blocker);
            }, length);
            if (shift <= EPS) continue;
            clips = clips.map(clip => (ids.has(clip.id) ? { ...clip, startSec: round3(Math.max(0, clip.startSec - shift)) } : clip));
        }
    }
    return { ...after, clips };
};

/**
 * Copy clips (each with what is linked to it) to right after themselves: a copied group lands
 * as soon after its original as all of its tracks are free, and after everything on those
 * tracks otherwise. Returns the project and the ids of the copies, to select them.
 */
export const duplicateClips = (project: QuickEditorProject, clipIds: string[], makeId: () => string): { project: QuickEditorProject; copies: string[] } => {
    const groups: EditorClip[][] = [];
    const taken = new Set<string>();
    for (const id of clipIds) {
        if (taken.has(id)) continue;
        const group = linkedGroup(project, id);
        if (group.length === 0) continue;
        for (const member of group) taken.add(member.id);
        groups.push(group);
    }
    let next = project;
    const copies: string[] = [];
    for (const group of groups) {
        const groupStart = Math.min(...group.map(c => c.startSec));
        const groupEnd = Math.max(...group.map(clipEnd));
        const tracks = Array.from(new Set(group.map(c => c.trackId)));
        const lastOnTracks = Math.max(groupEnd, ...next.clips.filter(c => tracks.includes(c.trackId)).map(clipEnd));
        const fits = (delta: number) => group.every(member => isFreeAt(next, member.trackId, member.startSec + delta, clipDuration(member)));
        const delta = [groupEnd - groupStart, lastOnTracks - groupStart].find(fits) ?? lastOnTracks - groupStart;
        const linkId = group.length > 1 ? makeId() : undefined;
        const made = group.map(member => ({
            ...member, id: makeId(), startSec: round3(member.startSec + delta), ...(linkId ? { linkId } : {}),
        }));
        if (!linkId) for (const copy of made) delete copy.linkId;
        copies.push(...made.map(c => c.id));
        next = { ...next, clips: [...next.clips, ...made] };
    }
    return { project: next, copies };
};

/** Whether these clips can be linked: at least two, not already all one linked group. */
export const canLinkClips = (project: QuickEditorProject, clipIds: string[]): boolean => {
    const clips = project.clips.filter(c => clipIds.includes(c.id));
    if (clips.length < 2) return false;
    const groups = new Set(clips.map(c => c.linkId ?? `solo:${c.id}`));
    return groups.size > 1;
};

/**
 * Link clips so they move, trim and cut together, like a video and the sound that came
 * with it. Whatever each clip was already linked to joins the new group.
 */
export const linkClips = (project: QuickEditorProject, clipIds: string[], makeId: () => string): QuickEditorProject => {
    if (!canLinkClips(project, clipIds)) return project;
    const members = new Set<string>();
    for (const id of clipIds) for (const member of linkedGroup(project, id)) members.add(member.id);
    const linkId = makeId();
    return { ...project, clips: project.clips.map(clip => (members.has(clip.id) ? { ...clip, linkId } : clip)) };
};

/** The quietest a clip can be set: at this level it is silent. */
export const MIN_GAIN_DB = -60;

/** A gain in decibels as a volume factor: 0 dB is 1, MIN_GAIN_DB and below is silence. */
export const dbToGain = (db: number): number => (db <= MIN_GAIN_DB ? 0 : Math.pow(10, Math.min(0, db) / 20));

export const clipGainDb = (clip: EditorClip): number => clip.gainDb ?? 0;

/** "0 dB", "-6.5 dB", or "Silent" at the bottom of the range. */
export const formatGainDb = (db: number): string => (db <= MIN_GAIN_DB ? 'Silent' : `${db === 0 ? '0' : db.toFixed(1)} dB`);

const isAudioClip = (project: QuickEditorProject, clip: EditorClip): boolean => trackOf(project, clip.trackId)?.kind === 'audio';

/**
 * The audio clips a gain change on these clips applies to: audio clips themselves, and for
 * a video clip the sound linked to it.
 */
export const gainTargets = (project: QuickEditorProject, clipIds: string[]): EditorClip[] => {
    const targets = new Map<string, EditorClip>();
    for (const id of clipIds) {
        const clip = project.clips.find(c => c.id === id);
        if (!clip) continue;
        if (isAudioClip(project, clip)) targets.set(clip.id, clip);
        else for (const member of linkedGroup(project, id)) if (isAudioClip(project, member)) targets.set(member.id, member);
    }
    return [...targets.values()];
};

/** Set the gain of these clips' sound, in decibels between MIN_GAIN_DB and 0. */
export const setClipGain = (project: QuickEditorProject, clipIds: string[], db: number): QuickEditorProject => {
    const ids = new Set(gainTargets(project, clipIds).map(c => c.id));
    if (ids.size === 0) return project;
    const value = Math.round(clamp(db, MIN_GAIN_DB, 0) * 10) / 10;
    return {
        ...project,
        clips: project.clips.map((clip) => {
            if (!ids.has(clip.id)) return clip;
            if (value === 0) {
                const { gainDb: _dropped, ...rest } = clip;
                return rest;
            }
            return { ...clip, gainDb: value };
        }),
    };
};

/** Separate a clip from its linked partner so each can be edited alone. */
export const unlinkClips = (project: QuickEditorProject, clipId: string): QuickEditorProject => {
    const ids = new Set(linkedGroup(project, clipId).map(c => c.id));
    if (ids.size <= 1) return project;
    return {
        ...project,
        clips: project.clips.map((clip) => {
            if (!ids.has(clip.id)) return clip;
            const { linkId: _dropped, ...rest } = clip;
            return rest;
        }),
    };
};

/** Timeline positions a dragged edge can snap to: clip edges, the start, and any extras (the playhead). */
export const snapPoints = (project: QuickEditorProject, ignoreIds: string[] = [], extra: number[] = []): number[] => {
    const ignore = new Set(ignoreIds);
    const points = [0, ...extra];
    for (const clip of project.clips) {
        if (ignore.has(clip.id)) continue;
        points.push(clip.startSec, clipEnd(clip));
    }
    return points;
};

/** `time`, or the nearest point within `thresholdSec` of it. */
export const snapTime = (time: number, points: number[], thresholdSec: number): number => {
    let best = time;
    let bestDistance = thresholdSec;
    for (const point of points) {
        const distance = Math.abs(point - time);
        if (distance <= bestDistance) {
            best = point;
            bestDistance = distance;
        }
    }
    return best;
};

/** Snap a moving clip so that its start or its end lands on a point, whichever is closer. */
export const snapMove = (start: number, duration: number, points: number[], thresholdSec: number): number => {
    const end = start + duration;
    const snappedStart = snapTime(start, points, thresholdSec);
    const snappedEnd = snapTime(end, points, thresholdSec);
    const startShift = Math.abs(snappedStart - start);
    const endShift = Math.abs(snappedEnd - end);
    const startSnaps = snappedStart !== start;
    const endSnaps = snappedEnd !== end;
    if (startSnaps && (!endSnaps || startShift <= endShift)) return snappedStart;
    if (endSnaps) return snappedEnd - duration;
    return start;
};

const contains = (clip: EditorClip, time: number): boolean => time >= clip.startSec - EPS && time < clipEnd(clip) - EPS;

/** The video clip shown at `time`: the one on the topmost visible track. */
export const activeVideoClip = (project: QuickEditorProject, time: number): EditorClip | null => {
    for (const track of project.tracks) {
        if (track.kind !== 'video' || track.hidden) continue;
        const clip = project.clips.find(c => c.trackId === track.id && contains(c, time));
        if (clip) return clip;
    }
    return null;
};

/**
 * The video layers seen at `time`, bottom first: a clip from each visible track that has
 * one there, each drawn over the ones before it at its track's opacity.
 */
export const activeVideoLayers = (project: QuickEditorProject, time: number): { clip: EditorClip; opacity: number }[] => {
    const layers: { clip: EditorClip; opacity: number }[] = [];
    for (const track of project.tracks) {
        if (track.kind !== 'video' || track.hidden) continue;
        const clip = project.clips.find(c => c.trackId === track.id && contains(c, time));
        if (clip) layers.unshift({ clip, opacity: trackLevel(track) });
    }
    return layers;
};

/** The audio clips heard at `time`: everything on tracks that are not muted. */
export const activeAudioClips = (project: QuickEditorProject, time: number): EditorClip[] => {
    const audible = new Set(project.tracks.filter(t => t.kind === 'audio' && !t.muted).map(t => t.id));
    return project.clips.filter(c => audible.has(c.trackId) && contains(c, time));
};

const without = (project: QuickEditorProject, ...keys: ('inSec' | 'outSec')[]): QuickEditorProject => {
    const next = { ...project };
    for (const key of keys) delete next[key];
    return next;
};

/** Mark the in point; an out point at or before it is dropped. */
export const setInPoint = (project: QuickEditorProject, atSec: number): QuickEditorProject => {
    const at = round3(clamp(onGrid(project, atSec), 0, projectDuration(project)));
    const next = { ...project, inSec: at };
    return project.outSec !== undefined && project.outSec <= at + MIN_CLIP_SEC ? without(next, 'outSec') : next;
};

/** Mark the out point; an in point at or after it is dropped. */
export const setOutPoint = (project: QuickEditorProject, atSec: number): QuickEditorProject => {
    const at = round3(clamp(onGrid(project, atSec), 0, projectDuration(project)));
    const next = { ...project, outSec: at };
    return project.inSec !== undefined && project.inSec >= at - MIN_CLIP_SEC ? without(next, 'inSec') : next;
};

export const clearInOut = (project: QuickEditorProject): QuickEditorProject => without(project, 'inSec', 'outSec');

/**
 * The part of the edit to export: from the in point (or the start) to the out point (or the
 * end), kept within the edit. `marked` tells whether in or out points narrow it.
 */
export const exportRange = (project: QuickEditorProject): { startSec: number; endSec: number; marked: boolean } => {
    const total = projectDuration(project);
    const start = clamp(project.inSec ?? 0, 0, total);
    const end = clamp(project.outSec ?? total, 0, total);
    if (end - start < MIN_CLIP_SEC) return { startSec: 0, endSec: round3(total), marked: false };
    return { startSec: round3(start), endSec: round3(end), marked: project.inSec !== undefined || project.outSec !== undefined };
};

/** m:ss:ff, the frame counted at `fps`, for the transport readout and the ruler. */
export const formatFrameTimecode = (seconds: number, fps: number): string => {
    const s = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
    const nominal = Math.max(1, Math.round(fps));
    const total = Math.round(s * fps);
    const whole = Math.floor(total / fps + 1e-9);
    const frame = Math.min(nominal - 1, Math.max(0, total - Math.round(whole * fps)));
    const minutes = Math.floor(whole / 60);
    return `${minutes}:${String(whole - minutes * 60).padStart(2, '0')}:${String(frame).padStart(2, '0')}`;
};

/** m:ss.t for lengths and marks. */
export const formatTimecode = (seconds: number): string => {
    const s = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
    const minutes = Math.floor(s / 60);
    const rest = s - minutes * 60;
    return `${minutes}:${rest < 10 ? '0' : ''}${rest.toFixed(1)}`;
};
